import { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

const PROJECT_FORMATS = ["Film (Feature)", "TV", "Commercial", "Short Film", "Theatre", "Voiceover"];
const ROLE_TYPES = ["Lead/Regular", "Supporting", "Recurring", "Day Player", "Background"];
const TRAITS = ["Confident", "Cocky", "Nervous", "Comedic", "Dramatic", "Aggressive", "Friendly", "Awkward"];
const AGE_RANGES = ["Under 18", "18–21", "22–25", "26–30", "31–35", "36–45", "46+"];
const GENRES = ["Romance", "Horror", "Comedy", "Drama", "Thriller", "Sci-Fi", "Fantasy", "Western", "Documentary", "Animation"];
const OUTCOMES = ["Callback", "Booked", "No Response", "Not Yet Known"];

const PLATFORM_FIELDS: ReadonlyArray<{ key: FieldKey; label: string; required?: boolean }> = [
  { key: "project_name", label: "Project Name", required: true },
  { key: "role_name", label: "Role", required: true },
  { key: "role_type", label: "Role Type" },
  { key: "age_range", label: "Age Range" },
  { key: "traits", label: "Character Traits" },
  { key: "project_format", label: "Project Format" },
  { key: "genres", label: "Genre" },
  { key: "company_name", label: "Company Name" },
  { key: "casting_director", label: "Casting Director" },
  { key: "audition_date", label: "Audition Date" },
  { key: "outcome", label: "Outcome" },
];

type FieldKey =
  | "project_name"
  | "role_name"
  | "role_type"
  | "age_range"
  | "traits"
  | "project_format"
  | "genres"
  | "company_name"
  | "casting_director"
  | "audition_date"
  | "outcome";


const IGNORE = "__ignore__";

// --- AI-light auto-mapping ---
function suggestField(header: string): FieldKey | null {
  const h = header.toLowerCase().replace(/[_\-\s]+/g, " ").trim();
  const tests: Array<[FieldKey, RegExp]> = [
    ["project_name", /\b(project|production|show|title|film)\b/],
    ["role_name", /\b(role|character|part)\b/],
    ["role_type", /\b(role.?type|type|billing)\b/],
    ["age_range", /\b(age)\b/],
    ["traits", /\b(trait|qualit|adjectiv|descrip|tone)/],
    ["project_format", /\b(format|medium|category)\b/],
    ["genres", /\b(genre|style)\b/],
    ["company_name", /\b(company|network|studio|prod\.?co|production company)\b/],
    ["casting_director", /\b(cd|casting.?director|casting)\b/],
    ["audition_date", /\b(date|when|day)\b/],
    ["outcome", /\b(outcome|result|status|booking)\b/],
  ];
  for (const [k, re] of tests) if (re.test(h)) return k;
  return null;
}

// --- normalizers ---
const norm = (v: unknown) => String(v ?? "").trim();
const lc = (v: unknown) => norm(v).toLowerCase();

function matchEnum(value: string, options: string[]): string | null {
  if (!value) return null;
  const v = value.toLowerCase().trim();
  for (const o of options) if (o.toLowerCase() === v) return o;
  for (const o of options) if (o.toLowerCase().includes(v) || v.includes(o.toLowerCase())) return o;
  return null;
}

function normalizeOutcome(v: string): string | null {
  const x = lc(v);
  if (!x) return null;
  if (/(book|cast|hired|won)/.test(x)) return "Booked";
  if (/call.?back/.test(x)) return "Callback";
  if (/(no.?response|none|nothing|pass|reject|declin)/.test(x)) return "No Response";
  if (/(pending|waiting|tbd|unknown)/.test(x)) return null;
  return matchEnum(v, OUTCOMES.filter((o) => o !== "Not Yet Known"));
}

function normalizeAge(v: string): string | null {
  const x = norm(v);
  if (!x) return null;
  const direct = matchEnum(x, AGE_RANGES);
  if (direct) return direct;
  // extract numbers
  const nums = x.match(/\d+/g)?.map(Number) ?? [];
  if (nums.length === 0) {
    if (/under|<\s*18|teen|child|kid/i.test(x)) return "Under 18";
    return null;
  }
  const lo = nums[0];
  const hi = nums[1] ?? lo;
  const mid = (lo + hi) / 2;
  if (mid < 18) return "Under 18";
  if (mid <= 21) return "18–21";
  if (mid <= 25) return "22–25";
  if (mid <= 30) return "26–30";
  if (mid <= 35) return "31–35";
  if (mid <= 45) return "36–45";
  return "46+";
}

function normalizeDate(v: unknown): string | null {
  if (v == null || v === "") return null;
  // Excel serial number
  if (typeof v === "number" && isFinite(v)) {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) {
      const mm = String(d.m).padStart(2, "0");
      const dd = String(d.d).padStart(2, "0");
      return `${d.y}-${mm}-${dd}`;
    }
  }
  const s = norm(v);
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function splitMulti(v: string): string[] {
  return v
    .split(/[,;|/]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeTraits(v: string): string[] {
  const parts = splitMulti(v);
  return parts.map((p) => matchEnum(p, TRAITS) ?? p);
}

function normalizeGenres(v: string): string[] {
  const parts = splitMulti(v);
  const out: string[] = [];
  for (const p of parts) {
    const m = matchEnum(p, GENRES);
    if (m && !out.includes(m)) out.push(m);
  }
  return out;
}

type Row = Record<string, unknown>;
type MappedRow = {
  project_name: string;
  role_name: string;
  role_type: string;
  age_range: string;
  traits: string[];
  genres: string[];
  project_format: string | null;
  company_name: string | null;
  casting_director: string | null;
  audition_date: string | null;
  outcome: string | null;
};

function transformRow(row: Row, mapping: Record<string, FieldKey | typeof IGNORE>): MappedRow | null {
  const get = (field: FieldKey): string => {
    for (const [col, target] of Object.entries(mapping)) {
      if (target === field) return norm(row[col]);
    }
    return "";
  };
  const getRaw = (field: FieldKey): unknown => {
    for (const [col, target] of Object.entries(mapping)) {
      if (target === field) return row[col];
    }
    return undefined;
  };

  const project_name = get("project_name");
  const role_name = get("role_name");
  if (!project_name && !role_name) return null;

  return {
    project_name: project_name || "Untitled",
    role_name: role_name || "Unknown",
    role_type: matchEnum(get("role_type"), ROLE_TYPES) ?? "Supporting",
    age_range: normalizeAge(get("age_range")) ?? "26–30",
    traits: normalizeTraits(get("traits")),
    genres: normalizeGenres(get("genres")),
    project_format: matchEnum(get("project_format"), PROJECT_FORMATS) ?? "Other",
    company_name: get("company_name") || null,
    casting_director: get("casting_director") || null,
    audition_date: normalizeDate(getRaw("audition_date")),
    outcome: normalizeOutcome(get("outcome")),
  };
}

type Step = "upload" | "map" | "preview" | "importing";

export function ImportAuditionsDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onImported: () => void;
}) {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>("upload");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [mapping, setMapping] = useState<Record<string, FieldKey | typeof IGNORE>>({});
  const [parsing, setParsing] = useState(false);

  function reset() {
    setStep("upload");
    setHeaders([]);
    setRows([]);
    setMapping({});
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleFile(file: File) {
    setParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: false });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<Row>(sheet, { defval: "", raw: true });
      if (json.length === 0) {
        toast.error("File appears to be empty");
        return;
      }
      const hs = Object.keys(json[0]);
      setHeaders(hs);
      setRows(json);
      const initial: Record<string, FieldKey | typeof IGNORE> = {};
      for (const h of hs) initial[h] = suggestField(h) ?? IGNORE;
      setMapping(initial);
      setStep("map");
    } catch {
      toast.error("Could not parse file. Try CSV or XLSX.");
    } finally {
      setParsing(false);
    }
  }

  const transformed = useMemo(() => {
    return rows
      .map((r) => transformRow(r, mapping))
      .filter((r): r is MappedRow => r !== null);
  }, [rows, mapping]);

  const skipped = rows.length - transformed.length;

  const requiredMapped = useMemo(() => {
    const targets = new Set(Object.values(mapping));
    return PLATFORM_FIELDS.filter((f) => f.required).every((f) => targets.has(f.key));
  }, [mapping]);

  async function doImport() {
    if (!user) return;
    setStep("importing");
    const records = transformed.map((r) => ({
      user_id: user.id,
      project_name: r.project_name,
      role_name: r.role_name,
      role_type: r.role_type,
      age_range: r.age_range,
      traits: r.traits,
      genres: r.genres,
      project_format: r.project_format,
      company_name: r.company_name,
      casting_director: r.casting_director,
      audition_date: r.audition_date,
      outcome: r.outcome,
    }));
    const { error } = await supabase.from("auditions").insert(records);
    if (error) {
      toast.error("Import failed. Please check your file and try again.");
      setStep("preview");
      return;
    }
    toast.success(
      `Imported ${records.length} audition${records.length === 1 ? "" : "s"}${
        skipped > 0 ? ` · ${skipped} skipped` : ""
      }`,
    );
    onImported();
    onOpenChange(false);
    reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import auditions</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file. We'll suggest column mappings — review and confirm.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div
            className="border-2 border-dashed border-border rounded-lg p-10 text-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
          >
            <FileSpreadsheet className="size-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              Drop a CSV or .xlsx file here, or
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            <Button onClick={() => fileRef.current?.click()} disabled={parsing}>
              {parsing ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
              Choose file
            </Button>
          </div>
        )}

        {step === "map" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {rows.length} rows detected. Map your columns to platform fields.
            </p>
            <div className="border border-border rounded-lg divide-y divide-border">
              {headers.map((h) => (
                <div key={h} className="grid grid-cols-2 gap-3 p-3 items-center">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{h}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      e.g. {String(rows[0]?.[h] ?? "").slice(0, 40) || "—"}
                    </div>
                  </div>
                  <Select
                    value={mapping[h] ?? IGNORE}
                    onValueChange={(v) =>
                      setMapping((m) => ({ ...m, [h]: v as FieldKey | typeof IGNORE }))
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={IGNORE}>— Ignore —</SelectItem>
                      {PLATFORM_FIELDS.map((f) => (
                        <SelectItem key={f.key} value={f.key}>
                          {f.label}
                          {f.required ? " *" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {!requiredMapped && (
              <p className="text-xs text-muted-foreground">
                Only Role and Project Name are required. Missing fields will use safe defaults and can be edited later.
              </p>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("upload")}>
                Back
              </Button>
              <Button onClick={() => setStep("preview")} disabled={!requiredMapped}>
                Preview
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <span className="text-foreground font-medium">{transformed.length}</span> ready to import
              {skipped > 0 && (
                <>
                  {" · "}
                  <span className="text-amber-600 dark:text-amber-400">{skipped} skipped (empty)</span>
                </>
              )}
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="max-h-[300px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Project</th>
                      <th className="text-left p-2 font-medium">Role</th>
                      <th className="text-left p-2 font-medium">Type</th>
                      <th className="text-left p-2 font-medium">Age</th>
                      <th className="text-left p-2 font-medium">Date</th>
                      <th className="text-left p-2 font-medium">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transformed.slice(0, 10).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2 truncate max-w-[140px]">{r.project_name}</td>
                        <td className="p-2 truncate max-w-[120px]">{r.role_name}</td>
                        <td className="p-2">{r.role_type}</td>
                        <td className="p-2">{r.age_range}</td>
                        <td className="p-2">{r.audition_date ?? "—"}</td>
                        <td className="p-2">{r.outcome ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {transformed.length > 10 && (
                <div className="p-2 text-xs text-muted-foreground bg-muted/30 border-t border-border">
                  Showing first 10 of {transformed.length}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("map")}>
                Back
              </Button>
              <Button onClick={doImport} disabled={transformed.length === 0}>
                Confirm import ({transformed.length})
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "importing" && (
          <div className="py-10 grid place-items-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground mt-3">Importing…</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
